import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MUITable, MUITableColumn } from 'components/common/MUITable/MUITable.component'
import { Stack, TableCell, Typography } from '@mui/material'
import { SubscriptionPeriod, WorkspaceMemberProfile, WorkspaceRole, WorkspaceSeatType } from 'models/user/userWorkspaceDTOs'
import { useChangeWorkspaceMemberRole, useGetTrialEligibility, useGetWorkspaceMembers } from 'dataQueries'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider/MUIDivider.component'
import { MUITooltip } from 'components/common/MUITooltip'
import Modal from 'components/common/Modals/Modal/Modal'
import { SystemMessage } from 'components/common/SystemMessage'
import { logAnalyticsEvent } from 'utils/analytics'
import { logger } from 'utils/logging'
import { usePlanPricing } from 'utils/hooks'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/** Admins whose seat the upgrade will convert to FULL (paid) — every non-FULL admin. */
export const getAdminsToPromote = (members: WorkspaceMemberProfile[]): WorkspaceMemberProfile[] =>
  members.filter((member) => member.role === WorkspaceRole.ADMIN && member.seatType !== WorkspaceSeatType.FULL)

/**
 * Paid seats the workspace will hold right after the upgrade: every admin (all promoted to FULL)
 * plus any member already on a billable (non-RESTRICTED) seat — mirrors the backend's
 * numberOfBillableSeats, where every seat that is not RESTRICTED bills at the per-seat price.
 */
export const countPaidSeatsAfterUpgrade = (members: WorkspaceMemberProfile[]): number =>
  members.filter((member) => member.role === WorkspaceRole.ADMIN || member.seatType !== WorkspaceSeatType.RESTRICTED).length

/** Applies staged demotions, so seat counts and guards reflect what Confirm will produce. */
export const applyStagedRoles = (
  members: WorkspaceMemberProfile[],
  stagedUserIds: ReadonlySet<string>
): WorkspaceMemberProfile[] =>
  members.map((member) => (stagedUserIds.has(member.userId) ? { ...member, role: WorkspaceRole.MEMBER } : member))

interface Props {
  /** Whether the modal is visible */
  isOpen: boolean
  /** Workspace being upgraded */
  workspaceId: string
  /** Billing period chosen in the plan card */
  period: SubscriptionPeriod
  /** Whether the checkout link is already being fetched */
  isLoading?: boolean
  /** Proceed to the Chargebee checkout */
  onConfirm: () => void
  /** Abort the upgrade */
  onClose: () => void
}

/**
 * Pre-checkout confirmation for a free → paid upgrade: on a paid plan every admin holds a FULL
 * (paid) seat, so the customer must see how many seats they are about to pay for and can demote
 * admins to members first to keep them free of charge. Demotions are staged and applied only when
 * the customer continues, so abandoning the dialog leaves the workspace untouched.
 * @example <UpgradeSeatConfirmationModal isOpen workspaceId={id} period={period} onConfirm={...} onClose={...} />
 */
export const UpgradeSeatConfirmationModal: FC<Props> = ({
  isOpen,
  workspaceId,
  period,
  isLoading = false,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation(['workspace_settings', 'common'])
  const { baseUserData } = useUserData()
  const { spawnErrorToast } = useSnackbar()
  const { currency, seatMonthlyPrice, formatAmount } = usePlanPricing()
  const country = baseUserData?.country

  const membersQuery = useGetWorkspaceMembers(workspaceId)
  const changeMemberRole = useChangeWorkspaceMemberRole()
  const { data: trialEligibility } = useGetTrialEligibility()

  const [stagedUserIds, setStagedUserIds] = useState<ReadonlySet<string>>(new Set())
  const [isApplying, setIsApplying] = useState(false)

  const members = useMemo(() => membersQuery.data ?? [], [membersQuery.data])
  const effectiveMembers = useMemo(() => applyStagedRoles(members, stagedUserIds), [members, stagedUserIds])

  const adminsToPromote = useMemo(() => getAdminsToPromote(members), [members])
  const adminCount = useMemo(
    () => effectiveMembers.filter((member) => member.role === WorkspaceRole.ADMIN).length,
    [effectiveMembers]
  )
  const paidSeatCount = useMemo(() => countPaidSeatsAfterUpgrade(effectiveMembers), [effectiveMembers])

  // The acting user performs the upgrade and keeps the admin seat — they are part of the paid-seat
  // count but must not be offered for demotion. `member.userId` is the platform UUID, so it matches
  // `baseUserData.internalId` (`baseUserData.id` is the Auth0 id); email is the fallback identity.
  const isActingUser = useCallback((member: WorkspaceMemberProfile): boolean =>
    (!!baseUserData?.internalId && member.userId === baseUserData.internalId) ||
    (!!baseUserData?.email && member.email.toLowerCase() === baseUserData.email.toLowerCase()),
  [baseUserData?.internalId, baseUserData?.email])

  // Fail closed: when /user/me carries neither identifier, nobody is provably "someone else",
  // so no demote button may be enabled — otherwise the acting admin could strip their own role.
  const isIdentityResolved = !!baseUserData?.internalId || !!baseUserData?.email

  const isSelfPromoted = useMemo(() => adminsToPromote.some(isActingUser), [adminsToPromote, isActingUser])
  const otherAdminsToPromote = useMemo(
    () => adminsToPromote.filter((member) => !isActingUser(member)),
    [adminsToPromote, isActingUser]
  )

  // A failed members fetch must not fail open: `members` collapses to [] which would render an
  // actionable modal claiming zero paid seats.
  const isMembersUnavailable = membersQuery.isError || (membersQuery.isSuccess && members.length === 0)

  const trialAllowed = !!trialEligibility?.trialAllowed

  useEffect(() => {
    if (membersQuery.isError) {
      logger.error('Failed to load members for the upgrade confirmation', membersQuery.error, {
        context: { component: 'UpgradeSeatConfirmationModal', action: 'loadMembers', metadata: { workspaceId } },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersQuery.isError])

  // One impression event per opening, emitted once the seat data it reports is actually loaded.
  const shownEventSent = useRef(false)
  useEffect(() => {
    if (isOpen && membersQuery.isSuccess && !shownEventSent.current) {
      shownEventSent.current = true
      logAnalyticsEvent('Upgrade Seat Confirmation Shown', {
        workspaceId,
        period,
        trialAllowed,
        currency,
        seatPrice: seatMonthlyPrice(period),
        paidSeatCount: countPaidSeatsAfterUpgrade(membersQuery.data ?? []),
      })
    }
  }, [isOpen, membersQuery.isSuccess, membersQuery.data, workspaceId, period, trialAllowed, currency, seatMonthlyPrice])

  const seatPrice = seatMonthlyPrice(period)
  const isYearly = period === SubscriptionPeriod.YEARLY
  // Seat prices are always per seat per month; yearly billing charges 12 months at once, so both
  // the line total and the unit price scale by 12 on the yearly period.
  const monthsBilled = isYearly ? 12 : 1
  const periodTotal = seatPrice !== null && paidSeatCount > 0
    ? formatAmount(seatPrice * monthsBilled * paidSeatCount)
    : null
  const unitPeriodPrice = seatPrice === null ? null : formatAmount(seatPrice * monthsBilled)
  // No published figure for the customer's currency must not leave a blank where the cost belongs,
  // and must never fall back to the other currency: say so and let Chargebee show the authoritative
  // amount, rather than blocking the upgrade.
  const isPriceUnavailable = paidSeatCount > 0 && seatPrice === null

  useEffect(() => {
    if (isPriceUnavailable) {
      logger.warn('No plan price is published for the customer currency', undefined, {
        context: {
          component: 'UpgradeSeatConfirmationModal',
          action: 'seatMonthlyPrice',
          metadata: { workspaceId, country, currency, paidSeatCount, period },
        },
      })
    }
  }, [isPriceUnavailable, workspaceId, country, currency, paidSeatCount, period])

  const isBusy = isLoading || isApplying

  const toggleStaged = (member: WorkspaceMemberProfile) => {
    const isStaged = stagedUserIds.has(member.userId)
    logAnalyticsEvent(
      isStaged ? 'Upgrade Seat Confirmation Undo Demote' : 'Upgrade Seat Confirmation Stage Demote',
      { workspaceId, memberId: member.userId }
    )
    setStagedUserIds((current) => {
      const next = new Set(current)
      if (isStaged) next.delete(member.userId)
      else next.add(member.userId)
      return next
    })
  }

  /** Applies the staged demotions in order, stopping at the first failure. */
  const applyStagedDemotions = async (): Promise<boolean> => {
    const staged = otherAdminsToPromote.filter((member) => stagedUserIds.has(member.userId))
    for (const member of staged) {
      try {
        await changeMemberRole.mutateAsync({
          workspaceId,
          userId: member.userId,
          role: WorkspaceRole.MEMBER,
          // Seat type is preserved; SPONSORED maps to RESTRICTED — an admin may only hold FULL or
          // RESTRICTED, and a free-plan workspace rejects any other seat.
          seatType: member.seatType === WorkspaceSeatType.SPONSORED ? WorkspaceSeatType.RESTRICTED : member.seatType,
          // Omitting canOrder would let the backend default it back to true, silently re-granting
          // ordering to a member who was deliberately barred.
          canOrder: member.canOrder,
        })
      } catch (error) {
        logger.error('Failed to apply a staged demotion before upgrade', error, {
          context: {
            component: 'UpgradeSeatConfirmationModal',
            action: 'applyStagedDemotions',
            metadata: { workspaceId, memberId: member.userId, membershipId: member.membershipId },
          },
        })
        spawnErrorToast(t('workspace_settings:plan.upgrade_confirmation.demote_error'))
        return false
      }
    }
    return true
  }

  const handleConfirm = async () => {
    // The last event before Chargebee takes over: it carries the figure the customer was quoted,
    // so a price that drifted from what Chargebee charged is visible against the invoice.
    logAnalyticsEvent('Upgrade Seat Confirmation Confirmed', {
      workspaceId,
      period,
      trialAllowed,
      currency,
      seatPrice,
      quotedTotal: periodTotal,
      paidSeatCount,
      demotedCount: stagedUserIds.size,
    })
    setIsApplying(true)
    const applied = await applyStagedDemotions()
    setIsApplying(false)
    if (applied) onConfirm()
  }

  const handleClose = () => {
    logAnalyticsEvent('Upgrade Seat Confirmation Cancelled', {
      workspaceId,
      period,
      discardedDemotions: stagedUserIds.size,
    })
    setStagedUserIds(new Set())
    onClose()
  }

  const demoteDisabledReason = (member: WorkspaceMemberProfile): string | undefined => {
    if (!isIdentityResolved) return t('workspace_settings:plan.upgrade_confirmation.demote_unavailable_tooltip')
    if (member.isOwner) return t('workspace_settings:plan.upgrade_confirmation.demote_owner_tooltip')
    if (adminCount === 1) return t('workspace_settings:plan.upgrade_confirmation.demote_sole_admin_tooltip')
    return undefined
  }

  const adminColumns: MUITableColumn<WorkspaceMemberProfile>[] = [
    {
      name: 'member',
      translation: t('workspace_settings:plan.upgrade_confirmation.admins_title'),
      size: '65%',
      mobileSize: '55%',
      isSortable: false,
      renderCell: ({ id, data: member }) => (
        <TableCell key={`${id}_member`}>
          <Stack minWidth={0}>
            <Typography variant="text-sm" fontWeight={500} noWrap>{member.name}</Typography>
            <Typography variant="text-xs" color={GRAY_700} noWrap>
              {stagedUserIds.has(member.userId)
                ? t('workspace_settings:plan.upgrade_confirmation.staged_note')
                : member.email}
            </Typography>
          </Stack>
        </TableCell>
      ),
    },
    {
      name: 'action',
      translation: '',
      size: '35%',
      mobileSize: '45%',
      isSortable: false,
      renderCell: ({ id, data: member }) => {
        const isStaged = stagedUserIds.has(member.userId)
        // A staged row is always undoable; the guards only govern staging a new demotion.
        const disabledReason = isStaged ? undefined : demoteDisabledReason(member)
        // Every row's visible label is identical, so the name goes into the accessible name, and
        // the lock reason with it — a tooltip on the wrapper is not announced for the button.
        const actionLabel = isStaged
          ? t('workspace_settings:plan.upgrade_confirmation.undo_aria', { name: member.name })
          : t('workspace_settings:plan.upgrade_confirmation.demote_aria', { name: member.name })
        return (
          <TableCell key={`${id}_action`} align="right">
            <MUITooltip placement="top" content={disabledReason}>
              <MUIButton
                type={isStaged ? 'secondaryNoBorder' : 'secondaryBorder'}
                size="sm"
                disabled={!!disabledReason || isBusy}
                aria-label={disabledReason ? `${actionLabel} — ${disabledReason}` : actionLabel}
                onClick={() => toggleStaged(member)}
              >
                {isStaged
                  ? t('workspace_settings:plan.upgrade_confirmation.undo')
                  : t('workspace_settings:plan.upgrade_confirmation.demote')}
              </MUIButton>
            </MUITooltip>
          </TableCell>
        )
      },
    },
  ]

  return (
    <Modal
      isOpen={isOpen}
      title={t('workspace_settings:plan.upgrade_confirmation.title')}
      onClose={handleClose}
      onClickOutside={handleClose}
      contentStyle={{ width: '56rem' }}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap="0.8rem" width="100%">
          <MUIButton type="secondaryNoBorder" disabled={isBusy} onClick={handleClose}>
            {t('common:Cancel')}
          </MUIButton>
          <MUIButton
            type="defaultPrimary"
            disabled={isBusy || membersQuery.isFetching || isMembersUnavailable}
            isLoading={isBusy}
            onClick={handleConfirm}
          >
            {t('workspace_settings:plan.upgrade_confirmation.confirm')}
          </MUIButton>
        </Stack>
      }
    >
      <Stack gap="1.6rem">
        {isMembersUnavailable && (
          <SystemMessage
            variant="error"
            message={t('workspace_settings:plan.upgrade_confirmation.load_error')}
          />
        )}

        <Stack gap="0.8rem">
          <Typography variant="text-sm" color={GRAY_700}>
            {t('workspace_settings:plan.upgrade_confirmation.message')}
          </Typography>

          {isSelfPromoted && (
            <Typography variant="text-sm" color={GRAY_700}>
              {t('workspace_settings:plan.upgrade_confirmation.self_note')}
            </Typography>
          )}
        </Stack>

        {otherAdminsToPromote.length > 0 && (
          <>
            <MUITable
              columns={adminColumns}
              data={otherAdminsToPromote.map((member) => ({ id: member.membershipId, data: member }))}
            />

            <SystemMessage
              variant="info"
              message={t('workspace_settings:plan.upgrade_confirmation.demote_hint')}
            />
          </>
        )}

        {!isMembersUnavailable && <MUIDivider margin={8} />}

        {!isMembersUnavailable && <Stack alignItems="flex-end" gap="0.4rem" marginBottom="0.4rem">
          <Stack direction="row" alignItems="flex-end" gap="1.2rem">
            <Typography variant="text-sm">
              {t('workspace_settings:plan.upgrade_confirmation.seats_label', { count: paidSeatCount })}
            </Typography>
            {!!periodTotal && (
              <Typography variant="text-sm" fontWeight="semiBold" color={GRAY_900}>
                {periodTotal}
              </Typography>
            )}
          </Stack>
          {!!unitPeriodPrice && (
            <Typography variant="text-sm" color={GRAY_700}>
              {isYearly
                ? t('workspace_settings:plan.upgrade_confirmation.unit_price_yearly', { unitPrice: unitPeriodPrice })
                : t('workspace_settings:plan.upgrade_confirmation.unit_price_monthly', { unitPrice: unitPeriodPrice })}
            </Typography>
          )}
          {isPriceUnavailable && (
            <Typography variant="text-sm" color={GRAY_700}>
              {t('workspace_settings:plan.upgrade_confirmation.price_unavailable')}
            </Typography>
          )}
          {trialAllowed && (
            <Typography variant="text-sm" fontWeight="semiBold" color={GRAY_900}>
              {t('workspace_settings:plan.upgrade_confirmation.trial_note')}
            </Typography>
          )}
        </Stack>}
      </Stack>
    </Modal>
  )
}
